import { useRef, useCallback, useState } from "react";
import { Skeleton } from "primereact/skeleton";
import InfiniteScroll from "react-infinite-scroll-component";
import useDateTime from "~/hooks/useDateTime";
import EmptyHistory from "~/components/audit-sidebar/EmptyHistory";
import type { DocumentAuditItem } from "./useDocumentHistory";
import { useDocumentHistory } from "./useDocumentHistory";
import Categories from "~/components/Processes/conversations/utils/categories.json";
import "./style.scss";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getDocumentHistoryEventLabel(item: DocumentAuditItem): string {
  if (item.action === "create") return "File Uploaded";
  if (item.action === "delete") return "File Deleted";
  if (item.action === "update") {
    const oldData = item.old_data ?? {};
    const newData = item.new_data ?? {};
    const keys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
    if (keys.has("description")) return "Description Updated";
    if (keys.has("year") || keys.has("month"))
      return "File Month & Year Updated";
    if (keys.has("category") || keys.has("subCategory"))
      return "Category & Subcategory Updated";
    // Publish/unpublish: source_context changes from "process" -> "core_entity" or vice versa
    if (keys.has("source_context")) {
      const newContext = String(newData.source_context ?? "");
      const oldContext = String(oldData.source_context ?? "");
      if (newContext === "core_entity" || oldContext === "process")
        return "File Published to Entity Files";
      if (newContext === "process" || oldContext === "core_entity")
        return "File Unpublished from Entity Files";
      return "File Published to Entity Files";
    }
    return "Details Updated";
  }
  return "Details Updated";
}

function formatMonthYear(data: Record<string, unknown>): string {
  const year = data?.year ?? data?.fileYear;
  let month = data?.month ?? data?.fileMonth;

  if (typeof month === "string" && month.includes("-") && !year) {
    const parts = month.split("-");
    if (parts.length === 2) {
      // Could be "YYYY-MM"
      const [y, m] = parts;
      const mNum = parseInt(m, 10);
      if (!Number.isNaN(mNum) && mNum >= 1 && mNum <= 12) {
        return `${MONTHS[mNum - 1]} ${y}`;
      }
    }
  }

  if (typeof month === "string" && month.includes("-")) {
    const parts = month.split("-");
    if (parts.length === 2 && parts[1]) {
      month = parts[1];
    }
  }

  if (!year && !month) return "None";
  let monthNum: number =
    typeof month === "string" ? parseInt(month, 10) : Number(month);

  // If month is not a number, try to parse as month name
  // if (Number.isNaN(monthNum) && typeof month === "string") {
  //   const monthIndex = MONTHS.findIndex(m => m.toLowerCase().startsWith(month.toLowerCase()));
  //   if (monthIndex !== -1) {
  //     monthNum = monthIndex + 1;
  //   }
  // }

  const yearStr = String(year ?? "");
  if (!Number.isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
    return `${MONTHS[monthNum - 1]} ${yearStr}`;
  }
  return yearStr || "None";
}
function resolveCategoryLabel(code: string | unknown): string {
  if (!code) return "";
  const codeStr = String(code);
  const cat = (
    Categories as Array<{
      code: string;
      name: string;
      subCategory?: Array<{ code: string; name: string }>;
    }>
  ).find((c) => c.code === codeStr);
  return cat?.name ?? codeStr;
}
function resolveSubCategoryLabel(
  catCode: string | unknown,
  subCode: string | unknown,
): string {
  if (!subCode) return "";
  const subCodeStr = String(subCode);
  const catCodeStr = String(catCode ?? "");

  if (catCodeStr) {
    const cat = (
      Categories as Array<{
        code: string;
        name: string;
        subCategory?: Array<{ code: string; name: string }>;
      }>
    ).find((c) => c.code === catCodeStr);
    const inCat = cat?.subCategory?.find((s) => s.code === subCodeStr);
    if (inCat) return inCat.name;
  }

  for (const c of Categories as Array<{
    code: string;
    name: string;
    subCategory?: Array<{ code: string; name: string }>;
  }>) {
    const found = c.subCategory?.find((s) => s.code === subCodeStr);
    if (found) return found.name;
  }

  return subCodeStr;
}
function formatCategorySubcategory(data: Record<string, unknown>): string {
  const cat = data?.category;
  const sub = data?.subCategory ?? data?.sub_category;
  const catLabel = resolveCategoryLabel(cat);
  const subLabel = resolveSubCategoryLabel(cat, sub);
  const parts = [catLabel, subLabel].filter(Boolean);
  return parts.length ? parts.join(" / ") : "None";
}

function safeDecode(value: unknown): string {
  if (value == null) return "";
  const str = String(value);
  try {
    return decodeURIComponent(str);
  } catch {
    return str;
  }
}

function extractYearFromName(name: string | undefined): string | undefined {
  if (!name) return undefined;
  // MM/YYYY format e.g. "06/2026"
  const slashMatch = name.match(/(\d{2})\/(\d{4})/);
  if (slashMatch) return slashMatch[2];
  // "Month YYYY" format e.g. "January 2026"
  const monthMatch = name.match(
    /(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})/,
  );
  if (monthMatch) return monthMatch[2];
  return undefined;
}

// Inject year into month-only data objects so formatMonthYear can render correctly
// Backend only stores month ("06"), year lives only in the file name
function injectYearIfMissing(
  data: Record<string, unknown>,
  name: string | undefined,
): Record<string, unknown> {
  if (!data || typeof data !== "object") return data;
  const hasMonth = "month" in data || "year" in data;
  if (!hasMonth) return data;
  if (data.year) return data; // already has year
  const year = extractYearFromName(name);
  if (!year) return data;
  return { ...data, year };
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      style={{
        flexShrink: 0,
        transform: open ? "rotate(0deg)" : "rotate(180deg)",
        transition: "transform 0.2s ease",
      }}
    >
      <path
        d="M10.4135 9.49934C10.315 9.49981 10.2173 9.48059 10.1263 9.44281C10.0353 9.40503 9.95275 9.34945 9.8835 9.27934L6.8835 6.27934L3.8835 9.27934C3.73823 9.34949 3.57421 9.37093 3.41579 9.34047C3.25736 9.31002 3.11298 9.22929 3.00407 9.11028C2.89517 8.99127 2.82754 8.84031 2.81122 8.67981C2.7949 8.51931 2.83077 8.35784 2.9135 8.21934L6.4135 4.71934C6.55413 4.57889 6.74475 4.5 6.9435 4.5C7.14225 4.5 7.33288 4.57889 7.4735 4.71934L10.9735 8.21934C11.114 8.35997 11.1928 8.55059 11.1928 8.74934C11.1928 8.94809 11.114 9.13871 10.9735 9.27934C10.9008 9.35318 10.8133 9.41092 10.7169 9.44882C10.6204 9.48672 10.517 9.50393 10.4135 9.49934Z"
        fill="#1F2937"
      />
    </svg>
  );
}

interface DocumentHistoryCompareProps {
  old_data: Record<string, unknown>;
  new_data: Record<string, unknown>;
  fileName?: string;
}

function DocumentHistoryCompare({
  old_data,
  new_data,
  fileName,
}: DocumentHistoryCompareProps) {
  const [open, setOpen] = useState(false);

  const oldKeys = Object.keys(old_data);
  const newKeys = Object.keys(new_data);
  const hasChanges = oldKeys.length > 0 || newKeys.length > 0;
  if (!hasChanges) return null;

  // const formatValue = (
  //   key: string,
  //   value: unknown,
  //   context: Record<string, unknown>,
  // ): string => {
  //   if (key === "description") return safeDecode(value);
  //   if (key === "year" || key === "month") {
  //     return formatMonthYear({
  //       month: context.month ?? value,
  //       year: context.year ?? value,
  //     });
  //   }
  //   if (key === "category" || key === "subCategory" || key === "sub_category") {
  //     return formatCategorySubcategory(context);
  //   }
  //   return value != null ? String(value) : "";
  // };

  const enrichedNewData = injectYearIfMissing(new_data, fileName);
  const enrichedOldData = injectYearIfMissing(old_data, fileName);

  // Keys that are internal metadata — never shown in Compare Changes UI
  const HIDDEN_KEYS = new Set(["source_context", "core_entity_info"]);

  const buildLines = (data: Record<string, unknown>, keys: string[]) => {
    const usedKeys = new Set<string>();
    const lines: { label: string; value: string }[] = [];

    for (const key of keys) {
      if (usedKeys.has(key)) continue;
      if (HIDDEN_KEYS.has(key)) {
        usedKeys.add(key);
        continue;
      }
      if (key === "month" || key === "year") {
        usedKeys.add("month");
        usedKeys.add("year");
        lines.push({
          label: "File Month & Year",
          value: formatMonthYear(data),
        });
      } else if (
        key === "category" ||
        key === "subCategory" ||
        key === "sub_category"
      ) {
        usedKeys.add("category");
        usedKeys.add("subCategory");
        usedKeys.add("sub_category");
        lines.push({
          label: "Category & Subcategory",
          value: formatCategorySubcategory(data),
        });
      } else if (key === "description") {
        usedKeys.add("description");
        lines.push({ label: "Description", value: safeDecode(data[key]) });
      } else {
        usedKeys.add(key);
        lines.push({
          label: key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (s) => s.toUpperCase()),
          value: data[key] != null ? String(data[key]) : "",
        });
      }
    }
    return lines;
  };

  const newLines = buildLines(enrichedNewData, newKeys);
  const oldLines = buildLines(enrichedOldData, oldKeys);

  return (
    <div className="document-history-compare">
      {/* ── Header — always visible ── */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: "16px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          gap: "8px",
        }}
      >
        <span
          style={{
            color: "var(--global-textColor, #1F2937)",
            fontFamily: "Inter",
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "150%",
          }}
        >
          Compare Changes
        </span>
        <ChevronIcon open={open} />
      </button>

      {open && (
        <div style={{ padding: "0 16px 16px" }}>
          <div
            className="change-card new-changes"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              paddingBottom: "12px",
              borderBottom: "1px solid var(--root-surface-border, #DFE7EF)",
              marginBottom: "12px",
            }}
          >
            <h6 className="title">Changes:</h6>
            {newLines.length > 0 ? (
              newLines.map(({ label, value }, i) => (
                <div key={`new-${i}`} className="changes-overflow">
                  <span>{label}:</span>
                  <span>{value || "-"}</span>
                </div>
              ))
            ) : (
              <div className="changes-overflow">
                <span>No changes</span>
              </div>
            )}
          </div>

          <div
            className="change-card old-changes"
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <h6 className="title">Previous:</h6>
            {oldLines.length > 0 ? (
              oldLines.map(({ label, value }, i) => (
                <div key={`old-${i}`} className="changes-overflow">
                  <span>{label}:</span>
                  <span>{value || "-"}</span>
                </div>
              ))
            ) : (
              <div className="changes-overflow">
                <span>None</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
function cleanDisplayName(name: string | undefined): string {
  if (!name) return "-";
  let cleaned = name.replace(/\(\(([^)]+)\)\)/g, "($1)");
  return cleaned;
}

function HistoryItem({
  item,
  fileNameFallback,
  isLast,
}: {
  item: DocumentAuditItem;
  fileNameFallback?: string;
  isLast: boolean;
}) {
  const { formatDateTime } = useDateTime();
  const displayName = cleanDisplayName(
    fileNameFallback || item.name || item.attachment,
  );
  const eventLabel = getDocumentHistoryEventLabel(item);
  const timestamp = item.createdAt ?? item.updatedAt ?? item.timestamp ?? "";
  const formattedDate = timestamp
    ? formatDateTime({
        date: new Date(timestamp),
        dateformat: "MMMM d, yyyy, h:mm a",
      })
    : "-";
  const actorLine =
    item.actor_email && item.actor_name
      ? `${item.actor_name} (${item.actor_email})`
      : item.actor_name || item.actor_email || "-";
  // Only treat as publish/unpublish if source_context is present
  // but no category/description/month changes in the same record
  const allKeys = new Set([
    ...Object.keys(item.new_data ?? {}),
    ...Object.keys(item.old_data ?? {}),
  ]);
  const isPublishUnpublish =
    item.action === "update" &&
    allKeys.has("source_context") &&
    !allKeys.has("category") &&
    !allKeys.has("sub_category") &&
    !allKeys.has("subCategory") &&
    !allKeys.has("description") &&
    !allKeys.has("month") &&
    !allKeys.has("year");

  const showCompare =
    item.action === "update" &&
    !isPublishUnpublish &&
    ((item.new_data && Object.keys(item.new_data).length > 0) ||
      (item.old_data && Object.keys(item.old_data).length > 0));

  return (
    <div className="document-history-item">
      <div className="document-history-item__timeline">
        <div className="document-history-item__marker" />
        {!isLast && (
          <div
            style={{
              width: "2px",
              flex: "1 0 0",
              background: "var(--tabview-nav-borderColor, #E5E7EB)",
              margin: "4px auto 0",
              minHeight: "24px",
            }}
          />
        )}
      </div>

      <div className="document-history-item__body">
        <h5 className="document-history-item__title">{eventLabel}</h5>
        <p className="document-history-item__file-name">{displayName}</p>
        <p className="document-history-item__actor">{actorLine}</p>
        <p className="document-history-item__date">
          {typeof formattedDate === "string"
            ? formattedDate
            : String(formattedDate ?? "")}
        </p>
        {showCompare &&
          item.old_data !== undefined &&
          item.new_data !== undefined && (
            <DocumentHistoryCompare
              old_data={item.old_data}
              new_data={item.new_data}
              fileName={item.name || fileNameFallback}
            />
          )}
      </div>
    </div>
  );
}

function HistorySkeleton() {
  return (
    <div className="document-history-skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="document-history-item">
          <div className="document-history-item__timeline">
            <div className="document-history-item__marker" />
            {i < 3 && (
              <div
                style={{
                  width: "2px",
                  flex: "1 0 0",
                  background: "#E5E7EB",
                  margin: "4px auto 0",
                  minHeight: "24px",
                }}
              />
            )}
          </div>
          <div className="document-history-item__body">
            <Skeleton width="12rem" className="mb-2" />
            <Skeleton width="100%" className="mb-2" />
            <Skeleton width="14rem" className="mb-2" />
            <Skeleton width="10rem" className="mb-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export interface DocumentHistoryProps {
  attachmentId: string | undefined;
  blobFilter?: string;
  objectType?: string;
  fileName?: string;
  enabled?: boolean;
}

export default function DocumentHistory({
  attachmentId,
  blobFilter,
  objectType,
  fileName,
  enabled = true,
}: DocumentHistoryProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    items,
    isLoading,
    isFetchingMore,
    hasMore,
    loadMore,
    isEmpty,
    error,
  } = useDocumentHistory({
    attachmentId,
    blobFilter,
    objectType,
    enabled: enabled && !!attachmentId,
  });

  const handleNext = useCallback(() => {
    loadMore();
  }, [loadMore]);

  if (!attachmentId) {
    return (
      <div className="document-history document-history--empty">
        <EmptyHistory />
      </div>
    );
  }

  if (error) {
    return (
      <div className="document-history document-history--error">
        <p className="document-history-error-message">
          Unable to load history. Please try again.
        </p>
      </div>
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <div className="document-history">
        <HistorySkeleton />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="document-history document-history--empty">
        <EmptyHistory />
      </div>
    );
  }

  return (
    <div className="document-history" ref={scrollRef}>
      <InfiniteScroll
        dataLength={items.length}
        next={handleNext}
        hasMore={hasMore}
        loader={
          isFetchingMore ? (
            <div className="document-history-loader">
              <i className="pi pi-spin pi-spinner" />
              <span>Loading more...</span>
            </div>
          ) : null
        }
        endMessage={null}
        height="calc(100vh - 280px)"
      >
        {items.map((item, index) => (
          <HistoryItem
            key={item.id}
            item={item}
            fileNameFallback={fileName}
            isLast={index === items.length - 1}
          />
        ))}
      </InfiniteScroll>
    </div>
  );
}
